/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Wallet & Credit Balance State Management Store (Zustand)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export interface WalletStateData {
  workspaceId: string | null;
  balance: number;
  reservedBalance: number;
  lifetimeCredits: number;
  monthlyQuota: number;
  updatedAt: number;
  isLoading: boolean;
  lastDeductionAmount: number | null;
  lastOptimisticTimestamp: number | null;
}

interface WalletStoreActions {
  setWallet: (
    data: Partial<Omit<WalletStateData, 'isLoading' | 'lastDeductionAmount' | 'lastOptimisticTimestamp'>>,
  ) => void;
  setLoading: (isLoading: boolean) => void;
  optimisticDeduct: (amount: number) => { success: boolean; newBalance: number };
  rollbackDeduct: (amount: number) => void;
  topupCredits: (amount: number) => void;
  resetWallet: () => void;
}

export type WalletStore = WalletStateData & WalletStoreActions;

export const DEFAULT_MONTHLY_QUOTA = 50000; // 50,000 standard credit quota

export const useWalletStore = create<WalletStore>((set, get) => ({
  workspaceId: null,
  balance: 25000, // Initial sample funded state
  reservedBalance: 0,
  lifetimeCredits: 50000,
  monthlyQuota: DEFAULT_MONTHLY_QUOTA,
  updatedAt: Math.floor(Date.now() / 1000),
  isLoading: false,
  lastDeductionAmount: null,
  lastOptimisticTimestamp: null,

  setWallet: (data) =>
    set((state) => ({
      ...state,
      ...data,
      balance: data.balance !== undefined ? data.balance : state.balance,
      monthlyQuota: data.monthlyQuota || state.monthlyQuota || DEFAULT_MONTHLY_QUOTA,
      updatedAt: data.updatedAt || Math.floor(Date.now() / 1000),
      isLoading: false,
    })),

  setLoading: (isLoading: boolean) => set({ isLoading }),

  optimisticDeduct: (amount: number) => {
    const current = get().balance;
    if (amount <= 0) {
      return { success: false, newBalance: current };
    }
    const newBalance = Math.max(0, Number((current - amount).toFixed(2)));
    set({
      balance: newBalance,
      lastDeductionAmount: amount,
      lastOptimisticTimestamp: Date.now(),
    });
    return { success: true, newBalance };
  },

  rollbackDeduct: (amount: number) => {
    if (amount <= 0) return;
    set((state) => ({
      balance: Number((state.balance + amount).toFixed(2)),
      lastDeductionAmount: null,
    }));
  },

  topupCredits: (amount: number) => {
    if (amount <= 0) return;
    set((state) => ({
      balance: Number((state.balance + amount).toFixed(2)),
      lifetimeCredits: Number((state.lifetimeCredits + amount).toFixed(2)),
      updatedAt: Math.floor(Date.now() / 1000),
    }));
  },

  resetWallet: () =>
    set({
      workspaceId: null,
      balance: 0,
      reservedBalance: 0,
      lifetimeCredits: 0,
      monthlyQuota: DEFAULT_MONTHLY_QUOTA,
      updatedAt: Math.floor(Date.now() / 1000),
      isLoading: false,
      lastDeductionAmount: null,
      lastOptimisticTimestamp: null,
    }),
}));
